"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { SearchContextType, SearchState } from "./types";
import { getAirports } from "../services/contexts/airport.service";
import { composeURL } from "../utils/urlComposer";
import { SERPResult } from "../types/serpResult";

// Hook auxiliar para extrair parâmetros da URL
const useQueryParams = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const defaultSearchState: SearchState = {
  tripType: "roundTrip",
  origin: null,
  destination: null,
  departureDates: [],
  returnDates: [],
  adults: 1,
  children: 0,
  babies: 0,
  isOriginVisible: false,
  isDestinationVisible: false,
  isSearchFinished: false,
  searchLoaderCurrentStep: 0,
  searchLoaderMaxSteps: 10,
  searchPercentResult: 0,
  filteredFlightResults: null,
  isShowTitleFrequentOrigins: true,
};

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const searchParams = useQueryParams();
  const [searchState, setSearchState] =
    useState<SearchState>(defaultSearchState);
  const {
    origins,
    nationalDestinations,
    internationalDestinations,
    findAirportByCode,
  } = getAirports();

  const updateStateFromParams = useCallback(() => {
    if (!searchParams) return;

    const params = Object.fromEntries(Array.from(searchParams.entries()));
    const {
      "origin-airport": originCode,
      "destination-airport": destinationCode,
      "departure-date": departureDates,
      "return-date": returnDates,
      "quantity-of-adults": adults,
      "quantity-of-children": children,
      "quantity-of-babies": babies,
      tripType,
    } = params;

    const validTripType =
      tripType === "oneWay" || tripType === "roundTrip"
        ? tripType
        : params["departure-date"]
          ? params["return-date"]
            ? "roundTrip"
            : "oneWay"
          : "roundTrip";

    const newState: SearchState = {
      ...searchState,
      tripType: validTripType,
      origin: originCode ? findAirportByCode(originCode) : null,
      destination: destinationCode ? findAirportByCode(destinationCode) : null,
      departureDates: departureDates ? departureDates.split(",") : [],
      returnDates: returnDates ? returnDates.split(",") : [],
      adults: adults ? Number(adults) : 1,
      children: children ? Number(children) : 0,
      babies: babies ? Number(babies) : 0,
    };

    if (JSON.stringify(searchState) !== JSON.stringify(newState)) {
      setSearchState(newState);
    }
  }, [searchParams]);

  useEffect(() => {
    updateStateFromParams();
  }, [updateStateFromParams]);

  const setFilteredFlightResults = useCallback(
    (
      updater:
        | SERPResult
        | null
        | ((previous: SERPResult | null) => SERPResult | null),
    ) => {
      setSearchState((previousState) => {
        const newValue =
          typeof updater === "function"
            ? updater(previousState.filteredFlightResults)
            : updater;

        return {
          ...previousState,
          filteredFlightResults: newValue,
        };
      });
    },
    [],
  );

  return (
    <SearchContext.Provider
      value={{
        searchState,
        origins,
        nationalDestinations,
        internationalDestinations,
        setTripType: (tripType) =>
          setSearchState((prev) => ({
            ...prev,
            tripType,
            returnDates: tripType === "oneWay" ? [] : prev.returnDates,
          })),
        setOrigin: (origin) =>
          setSearchState((prev) => ({
            ...prev,
            origin,
            isOriginVisible: false,
          })),
        setDestination: (destination) =>
          setSearchState((prev) => ({
            ...prev,
            destination,
            isDestinationVisible: false,
          })),
        setDepartureDates: (dates) =>
          setSearchState((prev) => ({ ...prev, departureDates: dates })),
        setReturnDates: (dates) =>
          setSearchState((prev) => ({ ...prev, returnDates: dates })),
        setAdults: (count) =>
          setSearchState((prev) => ({ ...prev, adults: count })),
        setChildren: (count) =>
          setSearchState((prev) => ({ ...prev, children: count })),
        setBabies: (count) =>
          setSearchState((prev) => ({ ...prev, babies: count })),
        setVisibility: (field, value) =>
          setSearchState((prev) => ({
            ...prev,
            isOriginVisible: field === "origin" ? value : prev.isOriginVisible,
            isDestinationVisible:
              field === "destination" ? value : prev.isDestinationVisible,
          })),
        updateStateFromParams,
        setStateSearchFinished: (value) =>
          setSearchState((prev) => ({ ...prev, isSearchFinished: value })),
        composeURL: () => composeURL(searchState),
        setSearchLoader: (currentStep, max) =>
          setSearchState((prev) => ({
            ...prev,
            searchLoaderCurrentStep: currentStep,
            searchLoaderMaxSteps: max,
          })),
        setSearchPercentResult: (searchPercent) =>
          setSearchState((prev) => ({
            ...prev,
            searchPercentResult: searchPercent,
          })),
        filteredFlightResults: searchState.filteredFlightResults,
        setFilteredFlightResults,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearchContext must be used within a SearchProvider");
  }
  return context;
};
